import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { content as ItemPropType } from 'lib/CustomPropTypes';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import AIMS_FLAVORS from 'lib/aimsFlavors';
import { LIVE_VIDEO_PID_MAP, STREAM_TO_SLATE_MAP, BREAKING_SLATE } from 'lib/liveVideo';
import { isIframeVideo } from 'lib/isIframeVideo';
import { LinkIfHref } from 'components/Link';
import TeasePicture from 'components/TeasePicture';
import VideoPlayer from 'components/VideoPlayer';
import { CvsdkTease } from 'components/CvsdkTease';
import DataVizLoader from 'components/DataVizLoader';
import './styles.themed.scss';

/**
 *
 * @param {object} item
 * @param {number} size
 * @param {string} isSportsVideo
 */
const picture = (item, size, isSportsVideo, shouldOpenLinkInNewTab) => {
  const defaultImageTransform = {
    s: AIMS_FLAVORS.FOCAL_762x508,
    m: AIMS_FLAVORS.FOCAL_412x275,
    l: AIMS_FLAVORS.FOCAL_500x333,
    xl: AIMS_FLAVORS.FOCAL_1000x667,
  };

  const smallImageTransform = {
    s: AIMS_FLAVORS.FOCAL_762x508,
    m: AIMS_FLAVORS.FOCAL_321x214,
    l: AIMS_FLAVORS.FOCAL_412x275,
    xl: AIMS_FLAVORS.FOCAL_500x333,
  };

  const IMAGE_TRANSFORMATIONS = {
    default: defaultImageTransform,
    small: smallImageTransform,
    large: defaultImageTransform,
  };

  const target = shouldOpenLinkInNewTab ? '_blank' : '';

  return (
    <LinkIfHref target={target} href={item?.computedValues?.url}>
      <TeasePicture
        responsiveFlavors={IMAGE_TRANSFORMATIONS[size] || IMAGE_TRANSFORMATIONS.default}
        type={item.type}
        computedValues={item.computedValues}
        hideCredit={size === 'small'}
        isSportsVideo={isSportsVideo}
      />
    </LinkIfHref>
  );
};

/**
 *
 * @param {object} packageMetadata
 */
const liveVideoPlayer = (packageMetadata) => (
  <CvsdkTease
    icid="storyline-tease"
    metadata={packageMetadata}
    showDek={false}
    showTitle
    showPlayerControls
    isStoryline
    stickyEnabled
  />
);

/**
 *
 * @param {object} item video content object with metadata
 * @param {boolean} isMutedAutoplayEnabled whether the sle-autoplay flag is enabled
 * @returns the corresponding inline video component with props set based off of curator metadata
 */
const inlineVideoPlayer = (item, isMutedAutoplayEnabled) => {
  const { item: video, computedValues = {}, metadata } = item;
  const { teaseImage = {} } = computedValues;

  const {
    playmakerAutoplayMuted,
    playmakerPlayBehavior,
    showHeadlineInlineVid = true,
    inlineHeadlineOverride,
  } = metadata || {};

  const autoPlay = isMutedAutoplayEnabled ? playmakerPlayBehavior === 'autoplay' : false;

  return (
    <VideoPlayer
      autoPlay={autoPlay}
      isEmbedded
      replay
      disableSticky
      onlyAutoplayMuted={playmakerAutoplayMuted ?? true}
      video={{ ...video, teaseImage }}
      isStoryline
      showHeadlineInlineVid={showHeadlineInlineVid}
      inlineHeadlineOverride={inlineHeadlineOverride}
    />
  );
};

/**
 *
 * @param {object} props
 * @param {object} [props.contentItem]
 * @param {packageMetadata} [props.packageMetadata]
 * @param {boolean} [props.isLiveBlogLayout]
 * @param {boolean} [props.shouldOpenLinkInNewTab]
 */
export function StorylineMedia({
  contentItem,
  packageMetadata = {},
  isLiveBlogLayout = false,
  shouldOpenLinkInNewTab = false,
}) {
  const {
    'sle-autoplay-muted': isMutedAutoplayEnabled,
  } = useFeatureFlagContext();

  const liveVideoPID = LIVE_VIDEO_PID_MAP[packageMetadata?.playmakerId];
  const liveVideoPIDOverride = LIVE_VIDEO_PID_MAP[packageMetadata?.playmakerIdOverride];

  const isIframe = isIframeVideo(packageMetadata?.playmakerId, packageMetadata?.playmakerIdOverride);

  const iframeVideo = packageMetadata?.playmakerId === 'other'
    ? liveVideoPIDOverride
    : liveVideoPID;

  /**
   *
   */
  async function loadData() {
    const script = document.createElement('script');
    script.src = iframeVideo;
    script.async = true;

    const videoEmbed = document.querySelector('.video-embed');

    videoEmbed.appendChild(script);
  }

  useEffect(() => {
    if (isIframe) {
      loadData();
    }
  }, []);

  if (!contentItem) return null;

  const {
    computedValues,
    item,
    type,
  } = contentItem;

  const {
    mediaSize: size = 'medium',
    playmakerTitle,
    playmakerToggle = false,
  } = packageMetadata;


  const isSportsVideoWorkflow = item?.taxonomy?.additionalTerms?.filter(({ termName }) => (
    termName?.display === 'Workflow: NBC Sports Video'
  ))?.length > 0;

  const isSportsVideo = isSportsVideoWorkflow ? 'video' : null;

  const isWhitespacePlaymakerTitle = playmakerTitle && (playmakerTitle?.trim()?.length === 0);
  const isVideo = type === 'video' || playmakerToggle === true;
  const isDataViz = type === 'custom';

  /**
   *
   */
  const getMedia = () => {
    if (isIframe) {
      const isBreaking = packageMetadata?.playmakerArt?.includes('breaking');
      const slate = isBreaking ? BREAKING_SLATE : STREAM_TO_SLATE_MAP[iframeVideo];

      return (
        <div className="iframe">
          <div className="video-embed">
            <img
              src={slate}
              alt=""
              className="iframe-slate"
            />
          </div>
          <div className="iframe-tease">
            {liveVideoPlayer(packageMetadata)}
          </div>
        </div>
      );
    }

    if (playmakerToggle) {
      return liveVideoPlayer(packageMetadata);
    }

    if (type === 'video') {
      return inlineVideoPlayer(contentItem, isMutedAutoplayEnabled);
    }

    if (isDataViz) {
      const isSportsVideoEmbed = computedValues?.url?.includes('nbcsports.com/video/embed')
        ? 'nbcSportsEmbed'
        : '';

      return (
        <DataVizLoader
          storylineDataViz
          data={contentItem}
          className={isSportsVideoEmbed}
        />
      );
    }

    return picture(contentItem, size, isSportsVideo, shouldOpenLinkInNewTab);
  };

  const storylineMediaClasses = classNames(
    'storyline-media',
    {
      'data-viz': isDataViz,
      'media-small': size === 'small' && !isVideo,
      'media-medium': size === 'medium' && !isVideo,
      'media-large': size === 'large' && !isVideo,
      'image-ratio': !(playmakerToggle || isVideo || isDataViz),
      'liveblog-layoutMedia': isLiveBlogLayout,
      'storyline-small-video': size === 'small' && isVideo,
      'storyline-medium-video': size === 'medium' && isVideo,
      'storyline-large-video': size === 'large' && isVideo,
      'live-video': playmakerToggle,
      'hide-playmaker-label': isWhitespacePlaymakerTitle,
    },
  );

  const storylineMediaTestId = playmakerToggle
    ? 'storyline-media-live'
    : `storyline-media-${type}`;

  return (
    <div
      className={storylineMediaClasses}
      data-testid={storylineMediaTestId}
    >
      {getMedia()}
    </div>
  );
}

StorylineMedia.propTypes = {
  contentItem: ItemPropType.isRequired,
  packageMetadata: PropTypes.shape({}),
  isLiveBlogLayout: PropTypes.bool,
  shouldOpenLinkInNewTab: PropTypes.bool,
};

StorylineMedia.defaultProps = {
  packageMetadata: {},
  isLiveBlogLayout: false,
};
